import React from "react";
import { Badge, Button, Callout, Card, Dialog, Field, Input, PageHeader, Select, Tabs } from "../components/DesignSystem";
import type { FloorPlanElement, FloorPlanElementType, ID, Scope, SeatingSummary, SeatingTable } from "../domain/types";
import { errorMessage } from "../services/errors";
import { usePlannerData } from "../state/PlannerDataContext";
import { ErrorState, LoadingState, useIdempotencyKey, useSubmitGuard } from "./common";

const FLOOR_PLAN_TYPES: { value: FloorPlanElementType; label: string }[] = [
  { value: "STAGE", label: "Stage" },
  { value: "DANCE_FLOOR", label: "Dance floor" },
  { value: "BUFFET", label: "Buffet" },
  { value: "BAR", label: "Bar" },
  { value: "DJ_SOUND", label: "DJ / sound" },
  { value: "ENTRANCE", label: "Entrance" },
  { value: "PRAYER_SPACE", label: "Prayer space" },
  { value: "GIFT_TABLE", label: "Gift table" },
  { value: "PHOTO_BACKDROP", label: "Photo backdrop" },
  { value: "DESSERT_STATION", label: "Dessert station" },
  { value: "OTHER", label: "Other" }
];

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(() => window.matchMedia("(max-width: 860px)").matches);
  React.useEffect(() => {
    const query = window.matchMedia("(max-width: 860px)");
    const onChange = () => setIsMobile(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);
  return isMobile;
}

export function SeatingScreen({ scope }: { scope: Scope }) {
  const { workspace, loading, error, service } = usePlannerData();
  const isMobile = useIsMobile();
  const [tab, setTab] = React.useState<"seating" | "floor-plan">("seating");
  const [eventId, setEventId] = React.useState<ID | "">(typeof scope === "string" && scope !== "all" && scope !== "shared" ? scope : "");
  const [summary, setSummary] = React.useState<SeatingSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = React.useState(false);
  const [summaryError, setSummaryError] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [addingTable, setAddingTable] = React.useState(false);
  const [addingElement, setAddingElement] = React.useState(false);
  const [pickerForTable, setPickerForTable] = React.useState<SeatingTable | null>(null);

  React.useEffect(() => {
    if (typeof scope === "string" && scope !== "all" && scope !== "shared") setEventId(scope);
  }, [scope]);

  const loadSummary = React.useCallback(async () => {
    if (!eventId) {
      setSummary(null);
      return;
    }
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      setSummary(await service.seating.getSummary(eventId));
    } catch (err) {
      setSummaryError(errorMessage(err));
    } finally {
      setSummaryLoading(false);
    }
  }, [service, eventId]);

  React.useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  if (loading || !workspace) return <div className="pob-pad"><LoadingState label="Loading seating" /></div>;
  if (error) return <div className="pob-pad"><ErrorState message={error} /></div>;

  const assign = async (personId: ID, tableId: ID) => {
    setActionError(null);
    try {
      await service.seating.assignSeat(eventId as ID, personId, tableId);
      await loadSummary();
    } catch (err) {
      setActionError(errorMessage(err));
    }
  };

  const unassign = async (assignmentId: ID) => {
    setActionError(null);
    try {
      await service.seating.removeAssignment(assignmentId);
      await loadSummary();
    } catch (err) {
      setActionError(errorMessage(err));
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Guests"
        title="Seating"
        meta={eventId ? [{ icon: "users", label: `${summary?.tables.length ?? 0} tables` }] : []}
        actions={eventId ? (
          tab === "seating"
            ? <Button variant="primary" icon="plus" onClick={() => setAddingTable(true)}>Add table</Button>
            : <Button variant="primary" icon="plus" onClick={() => setAddingElement(true)}>Add element</Button>
        ) : undefined}
        tabs={<Tabs value={tab} onChange={setTab} tabs={[{ value: "seating", label: "Seating" }, { value: "floor-plan", label: "Floor plan" }]} />}
      />
      <div className="pob-pad">
        <Field label="Celebration">
          <Select
            value={eventId}
            placeholder="Select a celebration"
            onChange={(event) => setEventId(event.target.value)}
            options={workspace.events.map((event) => ({ value: event.id, label: event.name }))}
            style={{ maxWidth: 320 }}
          />
        </Field>

        {!eventId && (
          <Callout tone="neutral" title="Select a celebration to see its seating chart" style={{ marginTop: "var(--space-4)" }}>
            Seating is always specific to one celebration — there is no whole-wedding seating chart. Pick one above to get started.
          </Callout>
        )}

        {eventId && summaryLoading && !summary && <LoadingState label="Loading seating chart" />}
        {eventId && summaryError && <ErrorState message={summaryError} retry={loadSummary} />}
        {actionError && <Callout tone="error" title="That didn't work" style={{ marginTop: "var(--space-4)", marginBottom: "var(--space-4)" }}>{actionError}</Callout>}

        {eventId && summary && (
          <>
            {(summary.warnings.unseatedConfirmedGuests.length > 0 || summary.warnings.overCapacityTables.length > 0) && (
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", margin: "var(--space-4) 0" }}>
                {summary.warnings.unseatedConfirmedGuests.length > 0 && (
                  <Callout tone="warning" title={`${summary.warnings.unseatedConfirmedGuests.length} confirmed guest${summary.warnings.unseatedConfirmedGuests.length === 1 ? "" : "s"} not yet seated`}>
                    {summary.warnings.unseatedConfirmedGuests.map((g) => g.name).join(", ")}
                  </Callout>
                )}
                {summary.warnings.overCapacityTables.length > 0 && (
                  <Callout tone="error" title="Some tables are over capacity">
                    {summary.warnings.overCapacityTables.map((t) => `${t.name} (${t.seated}/${t.capacity})`).join(", ")}
                  </Callout>
                )}
              </div>
            )}

            {tab === "seating"
              ? (isMobile
                  ? <MobileSeatingList summary={summary} onOpenTable={setPickerForTable} onUnassign={unassign} />
                  : <DesktopSeatingBoard summary={summary} onAssign={assign} onUnassign={unassign} />)
              : (isMobile
                  ? <MobileFloorPlanList elements={summary.floorPlanElements} service={service} onChanged={loadSummary} />
                  : <DesktopFloorPlanCanvas elements={summary.floorPlanElements} service={service} onChanged={loadSummary} />)}
          </>
        )}
      </div>

      {addingTable && eventId && (
        <TableDialog
          eventId={eventId as ID}
          onClose={() => setAddingTable(false)}
          onSaved={async () => { setAddingTable(false); await loadSummary(); }}
        />
      )}
      {addingElement && eventId && (
        <FloorPlanElementDialog
          eventId={eventId as ID}
          onClose={() => setAddingElement(false)}
          onSaved={async () => { setAddingElement(false); await loadSummary(); }}
        />
      )}
      {pickerForTable && summary && (
        <SeatPickerDialog
          table={pickerForTable}
          unseated={summary.warnings.unseatedConfirmedGuests}
          onAssign={async (personId) => { await assign(personId, pickerForTable.id); }}
          onUnassign={unassign}
          onClose={() => setPickerForTable(null)}
        />
      )}
    </div>
  );
}

function DesktopSeatingBoard({ summary, onAssign, onUnassign }: { summary: SeatingSummary; onAssign: (personId: ID, tableId: ID) => void; onUnassign: (assignmentId: ID) => void }) {
  const [dragPersonId, setDragPersonId] = React.useState<ID | null>(null);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: "var(--space-5)" }}>
      <Card eyebrow="Unseated" title="Confirmed, not seated" padding="var(--space-4)">
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
          {summary.warnings.unseatedConfirmedGuests.length === 0 && <span style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)" }}>Everyone confirmed is seated.</span>}
          {summary.warnings.unseatedConfirmedGuests.map((guest) => (
            <div
              key={guest.personId}
              draggable
              onDragStart={() => setDragPersonId(guest.personId)}
              onDragEnd={() => setDragPersonId(null)}
              style={{ padding: "var(--space-2) var(--space-3)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", cursor: "grab", font: "var(--type-body-sm)", background: "var(--surface-card)" }}
            >
              {guest.name}
            </div>
          ))}
        </div>
      </Card>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-4)", alignContent: "flex-start" }}>
        {summary.tables.length === 0 && <span style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)" }}>No tables yet — add one to start seating guests.</span>}
        {summary.tables.map((table) => {
          const overCapacity = table.assignments.length > table.capacity;
          return (
            <div
              key={table.id}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                if (dragPersonId) onAssign(dragPersonId, table.id);
                setDragPersonId(null);
              }}
              style={{ width: 220, border: `1px solid ${overCapacity ? "var(--error-border, #d33)" : "var(--border-subtle)"}`, borderRadius: "var(--radius-lg)", padding: "var(--space-3)", background: "var(--surface-card)" }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-2)" }}>
                <span style={{ font: "var(--type-label)" }}>{table.name}</span>
                <Badge tone={overCapacity ? "error" : "neutral"}>{table.assignments.length}/{table.capacity}</Badge>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)" }}>
                {table.assignments.map((assignment) => (
                  <div
                    key={assignment.id}
                    draggable
                    onDragStart={() => setDragPersonId(assignment.personId)}
                    onDragEnd={() => setDragPersonId(null)}
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 6px", borderRadius: "var(--radius-sm)", background: "var(--surface-sunken)", cursor: "grab" }}
                  >
                    <span style={{ font: "var(--type-body-sm)" }}>{assignment.personName}</span>
                    <button type="button" onClick={() => onUnassign(assignment.id)} aria-label={`Remove ${assignment.personName}`} style={{ border: 0, background: "transparent", cursor: "pointer", color: "var(--text-tertiary)" }}>×</button>
                  </div>
                ))}
                {table.assignments.length === 0 && <span style={{ font: "var(--type-body-sm)", color: "var(--text-tertiary)" }}>Drag a guest here</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MobileSeatingList({ summary, onOpenTable, onUnassign }: { summary: SeatingSummary; onOpenTable: (table: SeatingTable) => void; onUnassign: (assignmentId: ID) => void }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
      {summary.tables.length === 0 && <span style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)" }}>No tables yet — add one to start seating guests.</span>}
      {summary.tables.map((table) => {
        const overCapacity = table.assignments.length > table.capacity;
        return (
          <Card key={table.id} padding="var(--space-4)">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-2)" }}>
              <span style={{ font: "var(--type-label)" }}>{table.name}</span>
              <Badge tone={overCapacity ? "error" : "neutral"}>{table.assignments.length}/{table.capacity}</Badge>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)", marginBottom: "var(--space-3)" }}>
              {table.assignments.map((assignment) => (
                <div key={assignment.id} style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ font: "var(--type-body-sm)" }}>{assignment.personName}</span>
                  <button type="button" onClick={() => onUnassign(assignment.id)} style={{ border: 0, background: "transparent", color: "var(--text-tertiary)" }}>Remove</button>
                </div>
              ))}
              {table.assignments.length === 0 && <span style={{ font: "var(--type-body-sm)", color: "var(--text-tertiary)" }}>Nobody seated yet</span>}
            </div>
            <Button size="sm" onClick={() => onOpenTable(table)}>Assign guests</Button>
          </Card>
        );
      })}
    </div>
  );
}

function SeatPickerDialog({ table, unseated, onAssign, onUnassign, onClose }: {
  table: SeatingTable;
  unseated: { personId: ID; name: string }[];
  onAssign: (personId: ID) => Promise<void>;
  onUnassign: (assignmentId: ID) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = React.useState("");
  const filtered = unseated.filter((guest) => guest.name.toLowerCase().includes(query.toLowerCase()));
  return (
    <Dialog title={`Seat guests at ${table.name}`} onClose={onClose} footer={<Button size="sm" onClick={onClose}>Done</Button>}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {table.assignments.length > 0 && (
          <div>
            <span style={{ font: "var(--type-label)" }}>Already seated here</span>
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)", marginTop: "var(--space-2)" }}>
              {table.assignments.map((assignment) => (
                <div key={assignment.id} style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ font: "var(--type-body-sm)" }}>{assignment.personName}</span>
                  <button type="button" onClick={() => onUnassign(assignment.id)} style={{ border: 0, background: "transparent", color: "var(--text-tertiary)" }}>Remove</button>
                </div>
              ))}
            </div>
          </div>
        )}
        <Field label="Search confirmed guests"><Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search by name" /></Field>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)", maxHeight: 260, overflowY: "auto" }}>
          {filtered.length === 0 && <span style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)" }}>No unseated confirmed guests match.</span>}
          {filtered.map((guest) => (
            <button key={guest.personId} type="button" onClick={() => void onAssign(guest.personId)} style={{ textAlign: "left", padding: "var(--space-2) var(--space-3)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", background: "var(--surface-card)", cursor: "pointer", font: "var(--type-body-sm)" }}>
              {guest.name}
            </button>
          ))}
        </div>
      </div>
    </Dialog>
  );
}

function TableDialog({ eventId, onClose, onSaved }: { eventId: ID; onClose: () => void; onSaved: () => Promise<void> }) {
  const { service } = usePlannerData();
  const [name, setName] = React.useState("");
  const [capacity, setCapacity] = React.useState("10");
  const [formError, setFormError] = React.useState<string | null>(null);
  const { submitting, guardedSubmit } = useSubmitGuard();
  const idempotencyKey = useIdempotencyKey();

  const submit = () => guardedSubmit(async () => {
    setFormError(null);
    const parsedCapacity = Number(capacity);
    if (!name.trim()) { setFormError("Give the table a name."); return; }
    if (!Number.isFinite(parsedCapacity) || parsedCapacity < 1) { setFormError("Capacity must be at least 1."); return; }
    try {
      await service.seating.createTable(eventId, { name: name.trim(), capacity: parsedCapacity }, idempotencyKey);
      await onSaved();
    } catch (err) {
      setFormError(errorMessage(err));
    }
  });

  return (
    <Dialog
      title="Add table"
      onClose={onClose}
      footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" onClick={submit} disabled={submitting}>{submitting ? "Adding…" : "Add table"}</Button></>}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {formError && <Callout tone="error" title="Check the form">{formError}</Callout>}
        <Field label="Table name"><Input value={name} onChange={(event) => setName(event.target.value)} placeholder="Table 1" /></Field>
        <Field label="Capacity"><Input value={capacity} type="number" onChange={(event) => setCapacity(event.target.value)} /></Field>
      </div>
    </Dialog>
  );
}

function DesktopFloorPlanCanvas({ elements, service, onChanged }: { elements: FloorPlanElement[]; service: ReturnType<typeof usePlannerData>["service"]; onChanged: () => Promise<void> }) {
  const CELL = 32;
  const [dragging, setDragging] = React.useState<ID | null>(null);

  const move = async (elementId: ID, positionX: number, positionY: number) => {
    await service.seating.updateFloorPlanElement(elementId, { positionX: Math.max(0, positionX), positionY: Math.max(0, positionY) });
    await onChanged();
  };

  return (
    <div
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        const rect = event.currentTarget.getBoundingClientRect();
        const x = Math.round((event.clientX - rect.left) / CELL);
        const y = Math.round((event.clientY - rect.top) / CELL);
        if (dragging) void move(dragging, x, y);
        setDragging(null);
      }}
      style={{ position: "relative", height: 480, border: "1px dashed var(--border-subtle)", borderRadius: "var(--radius-lg)", background: "var(--surface-sunken)", overflow: "auto" }}
    >
      {elements.map((element) => (
        <div
          key={element.id}
          draggable
          onDragStart={() => setDragging(element.id)}
          style={{
            position: "absolute",
            left: element.positionX * CELL,
            top: element.positionY * CELL,
            width: element.width * CELL,
            height: element.height * CELL,
            border: "1px solid var(--border-subtle)",
            borderRadius: "var(--radius-md)",
            background: "var(--surface-card)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            font: "var(--type-body-sm)",
            cursor: "grab",
            textAlign: "center",
            padding: 4
          }}
        >
          {element.label}
          <button
            type="button"
            onClick={async () => { await service.seating.deleteFloorPlanElement(element.id); await onChanged(); }}
            aria-label={`Remove ${element.label}`}
            style={{ position: "absolute", top: 2, right: 2, border: 0, background: "transparent", color: "var(--text-tertiary)", cursor: "pointer" }}
          >
            ×
          </button>
        </div>
      ))}
      {elements.length === 0 && <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", font: "var(--type-body-sm)", color: "var(--text-secondary)" }}>Add stage, dance floor, buffet and other elements — drag to position them.</span>}
    </div>
  );
}

function MobileFloorPlanList({ elements, service, onChanged }: { elements: FloorPlanElement[]; service: ReturnType<typeof usePlannerData>["service"]; onChanged: () => Promise<void> }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
      {elements.length === 0 && <span style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)" }}>No floor plan elements yet.</span>}
      {elements.map((element) => (
        <Card key={element.id} padding="var(--space-4)">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ font: "var(--type-label)" }}>{element.label}</span>
            <button type="button" onClick={async () => { await service.seating.deleteFloorPlanElement(element.id); await onChanged(); }} style={{ border: 0, background: "transparent", color: "var(--text-tertiary)" }}>Remove</button>
          </div>
        </Card>
      ))}
    </div>
  );
}

function FloorPlanElementDialog({ eventId, onClose, onSaved }: { eventId: ID; onClose: () => void; onSaved: () => Promise<void> }) {
  const { service } = usePlannerData();
  const [type, setType] = React.useState<FloorPlanElementType>("STAGE");
  const [label, setLabel] = React.useState("Stage");
  const [formError, setFormError] = React.useState<string | null>(null);
  const { submitting, guardedSubmit } = useSubmitGuard();
  const idempotencyKey = useIdempotencyKey();

  const submit = () => guardedSubmit(async () => {
    setFormError(null);
    if (!label.trim()) { setFormError("Give the element a label."); return; }
    try {
      await service.seating.createFloorPlanElement(eventId, { type, label: label.trim(), width: 3, height: 2 }, idempotencyKey);
      await onSaved();
    } catch (err) {
      setFormError(errorMessage(err));
    }
  });

  return (
    <Dialog
      title="Add floor plan element"
      onClose={onClose}
      footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" onClick={submit} disabled={submitting}>{submitting ? "Adding…" : "Add element"}</Button></>}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {formError && <Callout tone="error" title="Check the form">{formError}</Callout>}
        <Field label="Type">
          <Select
            value={type}
            onChange={(event) => {
              const next = event.target.value as FloorPlanElementType;
              setType(next);
              setLabel(FLOOR_PLAN_TYPES.find((item) => item.value === next)?.label ?? "");
            }}
            options={FLOOR_PLAN_TYPES}
          />
        </Field>
        <Field label="Label"><Input value={label} onChange={(event) => setLabel(event.target.value)} /></Field>
      </div>
    </Dialog>
  );
}
